import { usePermissionsStore } from "../permissions/permissions_store";
// Import package components
import { useContext, useEffect, useState, useCallback, useMemo } from "react";
import { Image, Flex, Select, SelectOption } from "uikit-react";
import { useCookies } from "react-cookie";
import { useForm } from "react-hook-form";
import moment from "moment";
import classnames from "classnames";

import { Link } from "../../ui";

import { AppContexts } from "../../providers";
import EditUnitDetails from "./edit_unit_details";

const UnitDetails = () => {
  const { canEdit, canView } = usePermissionsStore();
  const canManageUnits = canEdit("admin.units");
  const [criteria, setCriteria] = useState({
    search_term: "",
    filters: "custom",
    preset: "departing",
    end_type: "check_in",
    sort: "updated_on",
    page: 0,
    tag: "all",
  });
  const [selectedUnit, setSelectedUnit] = useState(null);
  const {
    macros,
    getMacros
  } = useContext(AppContexts.ReportsContext);
  const { rental, getRental, getMacroLogs } = useContext(AppContexts.RentalsContext);
  const { get, settings } = useContext(AppContexts.SettingsContext);
  const { pmsFields, getPmsFields } = useContext(AppContexts.UtilContext);

  const rentalId = window.location.pathname.split("/")[2];

  useEffect(() => {
    document.title = "Unit Details - RueBaRue";
    getMacros(rentalId ? { search: rentalId, per: 100 } : undefined);
    get(["custom_macros"]);
    getPmsFields();
    if (rentalId) {
      getRental(rentalId);
    }
  }, []);

  const getMacroName = useCallback(
    (macro) => {
      if (!macro) return "";
      if (macro.category === "Unsynced") return macro.field;
      if (pmsFields?.[macro.category]?.[macro.field]) {
        return pmsFields[macro.category][macro.field];
      }
      return macro.field || macro.macro || "";
    },
    [pmsFields],
  );

  const reportRentalMacros = useMemo(() => {
    if (!macros) return {};
    const paginated = Array.isArray(macros?.paginated)
      ? macros.paginated
      : Array.isArray(macros?.[0]?.paginated)
        ? macros[0].paginated
        : [];
    const rentalData = paginated.find((r) => String(r.rental_id) === String(rentalId));
    return rentalData?.macros || {};
  }, [macros, rentalId]);

  const rentalMacros = useMemo(() => {
    const values = Array.isArray(rental?.custom_macro_values)
      ? rental.custom_macro_values
      : [];

    return values.reduce((lookup, value) => {
      lookup[String(value.macro_id)] = value.content ?? "";
      return lookup;
    }, { ...reportRentalMacros });
  }, [rental?.custom_macro_values, reportRentalMacros]);

  const filteredMacros = useMemo(() => {
    let list = [...(settings.custom_macros || [])];
    if (criteria.search_term) {
      const term = criteria.search_term.toLowerCase();
      list = list.filter((m) =>
        getMacroName(m).toLowerCase().includes(term) ||
        (m.macro || "").toLowerCase().includes(term)
      );
    }
    if (criteria.sort === "name") {
      list.sort((a, b) => getMacroName(a).localeCompare(getMacroName(b)));
    }
    return list;
  }, [settings.custom_macros, criteria.search_term, criteria.sort, getMacroName]);

  const handleEditUnitDetail = useCallback((macro) => {
    setSelectedUnit({
      ...macro,
      content: rentalMacros[String(macro.id)] || "",
      rental_id: rentalId,
    });
    getMacroLogs(macro.id, rentalId);
  }, [getMacroLogs, rentalId, rentalMacros]);

  const handleUnitDetailSaved = useCallback(async () => {
    await Promise.all([
      getRental(rentalId),
      getMacros({ search: rentalId, per: 100 }),
    ]);
  }, [getMacros, getRental, rentalId]);

  const handleCriteriaChange = useCallback((key, value) => {
    setCriteria((criteria) => ({ ...criteria, [key]: value }));
  });

  return (
    <>
      <section id="schedule-a-message">
        <div id="hms-page-title">
          <h1 className="uk-heading-small">Unit Details - {rental.pms_id}</h1>
        </div>
        <div id="hms-main-body">
          {canView("operations.property_defaults") && rental?.rental_id && <Link to={`/operations/property-defaults?rental_id=${rental.rental_id}`} className="uk-button uk-button-default uk-margin-bottom">Property Defaults</Link>}
          <div className="uk-card uk-card-small uk-card-default hms-form-card card-without-filters">
            <div className="uk-overflow-auto">
              <Flex alignment="between top" className="guest-form-top-filters">
                <div>
                  <form
                    action=""
                    className="uk-form uk-flex uk-flex-middle hms-form"
                  >
                    <div className="uk-inline">
                      <div className="uk-search uk-search-default">
                        {criteria.search_term && criteria.search_term !== "" ? (
                          <a
                            className="uk-form-icon-flip uk-form-icon"
                            uk-icon="icon: close"
                            onClick={() =>
                              handleCriteriaChange("search_term", "")
                            }
                          ></a>
                        ) : (
                          <span
                            className="uk-search-icon-flip"
                            uk-search-icon="true"
                          />
                        )}
                        <input
                          className="uk-search-input"
                          type="search"
                          placeholder="Name"
                          value={criteria.search_term}
                          onChange={(event) =>
                            handleCriteriaChange(
                              "search_term",
                              event.target.value,
                            )
                          }
                        />
                      </div>
                    </div>
                  </form>
                </div>
                <div>
                  <div className="hms-sortby-btn">
                    <button className="uk-button" type="button">
                      SORT BY<span uk-icon="icon: triangle-down"></span>
                    </button>
                    <div uk-dropdown="mode: click; offset: 0; animation: uk-animation-slide-top-small; duration: 400">
                      <ul className="uk-nav uk-dropdown-nav">
                        <li
                          className={classnames({
                            "uk-active": criteria.sort === "updated_on",
                          })}
                        >
                          <Link
                            href="#"
                            onClick={() =>
                              handleCriteriaChange("sort", "updated_on")
                            }
                          >
                            Last Updated
                          </Link>
                        </li>
                        <li
                          className={classnames({
                            "uk-active": criteria.sort === "name",
                          })}
                        >
                          <Link
                            href="#"
                            onClick={() => handleCriteriaChange("sort", "name")}
                          >
                            Name
                          </Link>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </Flex>
              <table className="uk-table uk-table-hover uk-table-divider hms-table">
                <thead>
                  <tr>
                    <th className="uk-table-expand">Field</th>
                    <th className="uk-table-expand">Macro</th>
                    <th className="uk-table-expand">Value</th>
                    <th className="uk-table-expand">Type</th>
                    <th className="actions">ACTIONS</th>
                  </tr>
                </thead>
                <tbody>
                    {
                      filteredMacros.map((macro) => {
                        return (
                          <tr key={macro.id}>
                            <td>
                                <div className="semi-bold-font">{getMacroName(macro)}</div>
                            </td>
                            <td>{macro.macro}</td>
                            <td>{rentalMacros[String(macro.id)] || ""}</td>
                            <td>{macro.category === 'Unsynced' ? 'Custom Field' : 'Import Field'}</td>
                            <td className="hms-action-btn">
                              <div className="uk-flex uk-flex-top">
                                <div className="uk-inline">
                                  {canManageUnits && (<button
                                    className="uk-button"
                                    uk-toggle="target: #edit-unit-details"
                                    onClick={() => handleEditUnitDetail(macro)}
                                    uk-tooltip="Edit"
                                  >
                                    <Image src="/images/edit.svg" />
                                  </button>)}
                                </div>
                              </div>
                            </td>
                          </tr>     
                        )
                      })
                    }        
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>
      {canManageUnits && (<EditUnitDetails unit={selectedUnit} onSaved={handleUnitDetailSaved} />)}
    </>
  );
};

export default UnitDetails;
